import { useCallback, useEffect, useRef, useState } from "react";
import {
    diktatNutzbar,
    diktatStatus,
    diktatStatusBeobachten,
    diktatTranskribieren,
    meldungAus,
    type DiktatStatus,
} from "../../services/diktatService";

/**
 * Diktieren: Mikrofon aufnehmen → ans Backend → Text zurück.
 *
 *   bereit → (Klick) startet → aufnahme (Zeit läuft) → (Klick / Höchstdauer)
 *   umwandeln → bereit, Text geht an `onText`.
 *
 * Das Mikrofon wird je Aufnahme frisch geöffnet und danach wieder freigegeben,
 * damit Windows/Edge nicht dauerhaft „Mikrofon in Verwendung“ zeigt.
 */

export type DiktatPhase = "bereit" | "startet" | "aufnahme" | "umwandeln";

export type DiktatHinweisArt =
    | "verweigert"
    | "kein-mikrofon"
    | "belegt"
    | "nicht-unterstuetzt"
    | "nichts-erkannt"
    | "fehler";

export interface DiktatHinweis {
    art: DiktatHinweisArt;
    meldung?: string;
}

/** Bevorzugte Formate: Edge/Chrome nehmen Opus in WebM, Safari nur MP4/AAC. */
export const DIKTAT_FORMATE = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
] as const;

/** Das erste Format, das der Browser aufnehmen kann — oder "" (Browser wählt selbst). */
export function waehleFormat(rec: { isTypeSupported?: (t: string) => boolean } | undefined): string {
    if (!rec?.isTypeSupported) return "";
    for (const f of DIKTAT_FORMATE) {
        try {
            if (rec.isTypeSupported(f)) return f;
        } catch {
            /* weiter */
        }
    }
    return "";
}

/** Fehler von getUserMedia → verständliche Hinweisart. */
export function hinweisFuerMikrofonFehler(err: unknown): DiktatHinweisArt {
    const name = (err as { name?: string })?.name ?? "";
    if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") return "verweigert";
    if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") return "kein-mikrofon";
    if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") return "belegt";
    return "fehler";
}

export interface UseDiktatOptionen {
    /** Erkannter Text (schon getrimmt, nie leer). */
    onText: (text: string) => void;
    /** Kurz bevor die Aufnahme startet — die Stelle zum Einfügen merken. */
    onStart?: () => void;
}

export function useDiktat({ onText, onStart }: UseDiktatOptionen) {
    const [status, setStatus] = useState<DiktatStatus | null>(null);
    const [phase, setPhase] = useState<DiktatPhase>("bereit");
    const [sekunden, setSekunden] = useState(0);
    const [hinweis, setHinweis] = useState<DiktatHinweis | null>(null);

    const recorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const teileRef = useRef<Blob[]>([]);
    const uhrRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const abbruchRef = useRef<AbortController | null>(null);
    const lebtRef = useRef(true);
    const onTextRef = useRef(onText);
    onTextRef.current = onText;
    const onStartRef = useRef(onStart);
    onStartRef.current = onStart;
    const maxRef = useRef(300);

    useEffect(() => {
        lebtRef.current = true;
        let aktiv = true;
        void diktatStatus().then((s) => {
            if (aktiv) setStatus(s);
        });
        const weg = diktatStatusBeobachten((s) => setStatus(s));
        return () => {
            aktiv = false;
            lebtRef.current = false;
            weg();
            if (uhrRef.current) clearInterval(uhrRef.current);
            abbruchRef.current?.abort();
            const rec = recorderRef.current;
            if (rec && rec.state !== "inactive") {
                rec.onstop = null;
                try {
                    rec.stop();
                } catch {
                    /* egal */
                }
            }
            streamRef.current?.getTracks().forEach((t) => t.stop());
        };
    }, []);

    useEffect(() => {
        if (status) maxRef.current = status.maxSekunden;
    }, [status]);

    const mikrofonFrei = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
    };

    const umwandeln = useCallback(async (audio: Blob) => {
        if (!audio.size) {
            setPhase("bereit");
            return;
        }
        setPhase("umwandeln");
        const ctrl = new AbortController();
        abbruchRef.current = ctrl;
        try {
            const text = (await diktatTranskribieren(audio, ctrl.signal)).trim();
            if (!lebtRef.current) return;
            if (text) onTextRef.current(text);
            else setHinweis({ art: "nichts-erkannt" });
        } catch (err) {
            if (!lebtRef.current || ctrl.signal.aborted) return;
            setHinweis({ art: "fehler", meldung: meldungAus(err) });
        } finally {
            if (lebtRef.current) setPhase("bereit");
        }
    }, []);

    const stop = useCallback(() => {
        if (uhrRef.current) {
            clearInterval(uhrRef.current);
            uhrRef.current = null;
        }
        const rec = recorderRef.current;
        if (rec && rec.state !== "inactive") rec.stop();
    }, []);

    const start = useCallback(async () => {
        setHinweis(null);
        const md = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
        const Rec = typeof window !== "undefined" ? window.MediaRecorder : undefined;
        if (!md?.getUserMedia || !Rec) {
            setHinweis({ art: "nicht-unterstuetzt" });
            return;
        }
        onStartRef.current?.();
        setPhase("startet");
        let stream: MediaStream;
        try {
            stream = await md.getUserMedia({ audio: true });
        } catch (err) {
            if (lebtRef.current) {
                setPhase("bereit");
                setHinweis({ art: hinweisFuerMikrofonFehler(err) });
            }
            return;
        }
        if (!lebtRef.current) {
            stream.getTracks().forEach((t) => t.stop());
            return;
        }
        streamRef.current = stream;
        const format = waehleFormat(Rec);
        let rec: MediaRecorder;
        try {
            rec = format ? new Rec(stream, { mimeType: format }) : new Rec(stream);
        } catch {
            mikrofonFrei();
            setPhase("bereit");
            setHinweis({ art: "nicht-unterstuetzt" });
            return;
        }
        teileRef.current = [];
        rec.ondataavailable = (e: BlobEvent) => {
            if (e.data && e.data.size) teileRef.current.push(e.data);
        };
        rec.onstop = () => {
            mikrofonFrei();
            recorderRef.current = null;
            const typ = rec.mimeType || format || "audio/webm";
            const audio = new Blob(teileRef.current, { type: typ });
            teileRef.current = [];
            void umwandeln(audio);
        };
        recorderRef.current = rec;
        rec.start(1000);
        setSekunden(0);
        setPhase("aufnahme");
        const beginn = Date.now();
        uhrRef.current = setInterval(() => {
            const s = Math.floor((Date.now() - beginn) / 1000);
            setSekunden(s);
            if (s >= maxRef.current) stop();
        }, 250);
    }, [stop, umwandeln]);

    const umschalten = useCallback(() => {
        if (phase === "aufnahme") stop();
        else if (phase === "bereit") void start();
    }, [phase, start, stop]);

    return {
        /** Knopf zeigen? Nur wenn eingerichtet UND eingeschaltet. */
        nutzbar: diktatNutzbar(status),
        status,
        phase,
        sekunden,
        maxSekunden: status?.maxSekunden ?? 300,
        hinweis,
        hinweisSchliessen: () => setHinweis(null),
        start,
        stop,
        umschalten,
    };
}
